export function isGoalSatisfied(value, target) {
  const current = Number(value);
  const required = Number(target);
  return Number.isFinite(current) && Number.isFinite(required) && current >= required;
}

// The resolver-owned score is also exposed as React state for rendering. Keep
// score-goal UI and completion evaluation on one monotonic snapshot while
// preserving all other run facts unchanged.
export function canonicalGoalProgress(progress = {}, liveScore = 0) {
  const source = progress && typeof progress === 'object' ? progress : {};
  const current = Number(source.score);
  const live = Number(liveScore);
  return {
    ...source,
    score: Math.max(
      Number.isFinite(current) ? current : 0,
      Number.isFinite(live) ? live : 0
    ),
  };
}

function normalizeGoalType(rawType) {
  const type = String(rawType || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (type === 'score' || type === 'score_reach' || type === 'reach_score') return 'score';
  if (type === 'activate_stripe' || type === 'stripe' || type === 'stripes' || type === 'stripes_activated') return 'stripesActivated';
  if (type === 'make_star' || type === 'star' || type === 'stars' || type === 'stars_created') return 'starsCreated';
  if (type === 'cascade' || type === 'cascades' || type === 'cascades_reach') return 'cascades';
  if (type === 'clear_element_fire' || type === 'clear_fire') return 'clearedFire';
  if (type === 'clear_element_water' || type === 'clear_water') return 'clearedWater';
  if (type === 'clear_element_air' || type === 'clear_air') return 'clearedAir';
  if (type === 'clear_element_earth' || type === 'clear_earth') return 'clearedEarth';
  return '';
}

// The one canonical snapshot consumed by both the goal panel and completion
// lifecycle. Score is always sourced from the live resolver score when it is
// newer than an asynchronous progress snapshot.
export function deriveCanonicalGoalState({ goals = [], score = 0, runProgress = {}, carryProgress = {} } = {}) {
  const run = runProgress && typeof runProgress === 'object' ? runProgress : {};
  const carry = carryProgress && typeof carryProgress === 'object' ? carryProgress : {};
  const merged = { ...carry, ...run };
  const progress = canonicalGoalProgress(merged, score);
  const list = Array.isArray(goals) ? goals : [];
  const canonicalGoals = list.map((goal) => {
    const type = normalizeGoalType(goal?.type || goal?.goalType || goal?.kind || goal?.metric || goal?.id);
    const target = Math.max(0, Number(goal?.target ?? goal?.value ?? goal?.amount ?? 0) || 0);
    const current = type === 'score'
      ? Number(progress.score) || 0
      : (typeof goal?.getCurrent === 'function' ? Number(goal.getCurrent(progress)) || 0 : Number(progress[type]) || 0);
    const done = type === 'score'
      ? isGoalSatisfied(current, target)
      : (typeof goal?.isDone === 'function' ? Boolean(goal.isDone(progress)) : isGoalSatisfied(current, target));
    return { ...goal, canonicalType: type, canonicalCurrent: current, canonicalDone: done };
  });
  return {
    progress,
    goals: canonicalGoals,
    done: canonicalGoals.reduce((count, goal) => count + (goal.canonicalDone ? 1 : 0), 0),
    total: canonicalGoals.length,
  };
}
